import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  StyleSheet,
  Alert,
  Switch,
  Platform,
  FlatList,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, DatePickerField, ContactSelector } from '../components';
import type { ContactSelectorMode, ContactSelectorVariant } from '../components';
import { ProductSelectorModal } from '../components/catalogues/ProductSelectorModal';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { apiService } from '../services/api';
import { ZoneMapPicker, ZoneResult } from '../components/ZoneMapPicker';

const PRODUCT_TYPES = ['physical', 'digital', 'service'] as const;
type ProductType = typeof PRODUCT_TYPES[number];

// ─── Types ────────────────────────────────────────────────────────────────────

interface NamedItem { id: string; name: string; }

interface CouponZone {
  name: string;
  mode?: 'country' | 'city' | 'radius';
  cities?: string[];
  polygon?: [number, number][];
  polygons?: [number, number][][];
  center?: { latitude: number; longitude: number };
  radiusKm?: number;
  value?: number;
  type?: 'percentage' | 'flat';
  enabled?: boolean;
}

interface CouponForm {
  code: string;
  type: 'percentage' | 'flat';
  value: string;
  minOrderAmount: string;
  startsAt: Date | null;
  expiresAt: Date | null;
  isPublic: boolean;
  firstTimeOnly: boolean;
  maxUsageTotal: string;
  maxUsagePerContact: string;
  productTypes: ProductType[];
  contacts: NamedItem[];
  products: NamedItem[];
  catalogues: NamedItem[];
  isActive: boolean;
  zones: CouponZone[];
}

const EMPTY_FORM: CouponForm = {
  code: '', type: 'flat', value: '', minOrderAmount: '',
  startsAt: null, expiresAt: null,
  isPublic: true, firstTimeOnly: false,
  maxUsageTotal: '', maxUsagePerContact: '',
  productTypes: [],
  contacts: [], products: [], catalogues: [],
  isActive: true, zones: [],
};

function couponToForm(c: any): CouponForm {
  return {
    code: c.code ?? '',
    type: c.type ?? 'flat',
    value: String(c.value ?? ''),
    minOrderAmount: c.minOrderAmount != null ? String(c.minOrderAmount) : '',
    startsAt: c.startsAt ? new Date(c.startsAt) : null,
    expiresAt: c.expiresAt ? new Date(c.expiresAt) : null,
    isPublic: c.isPublic ?? true,
    firstTimeOnly: c.firstTimeOnly ?? false,
    maxUsageTotal: c.maxUsageTotal != null ? String(c.maxUsageTotal) : '',
    maxUsagePerContact: c.maxUsagePerContact != null ? String(c.maxUsagePerContact) : '',
    productTypes: c.productTypes ?? [],
    // IDs stored on coupon — names resolved lazily when opening edit form
    contacts: (c.contactIds ?? []).map((id: string) => ({ id, name: id })),
    products: (c.productIds ?? []).map((id: string) => ({ id, name: id })),
    catalogues: (c.catalogueIds ?? []).map((id: string) => ({ id, name: id })),
    isActive: c.isActive ?? true,
    zones: c.zones ?? [],
  };
}

// ─── Generic searchable multi-picker modal ────────────────────────────────────

interface MultiPickerProps {
  visible: boolean;
  title: string;
  selected: NamedItem[];
  items: NamedItem[];
  loading: boolean;
  search: string;
  onSearchChange: (q: string) => void;
  onClose: () => void;
  onConfirm: (items: NamedItem[]) => void;
  renderSub?: (item: NamedItem) => string | undefined;
}

const MultiPicker: React.FC<MultiPickerProps> = ({
  visible, title, selected, items, loading,
  search, onSearchChange, onClose, onConfirm, renderSub,
}) => {
  const mp = useThemedStyles(getMp);
  const s = useThemedStyles(getS);
  const [picked, setPicked] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (visible) {
      const m = new Map<string, string>();
      selected.forEach(s => m.set(s.id, s.name));
      setPicked(m);
    }
  }, [visible]);

  const toggle = (item: NamedItem) =>
    setPicked(prev => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item.name);
      return next;
    });

  const confirm = () => {
    onConfirm(Array.from(picked.entries()).map(([id, name]) => ({ id, name })));
    onClose();
  };

  const content = (
    <View style={{ flex: 1, backgroundColor: colors.background.surface }}>
      <View style={mp.header}>
        <Text style={mp.title}>{title}</Text>
        <TouchableOpacity onPress={onClose} activeOpacity={0.7}>
          <Ionicons name="close" size={24} color={colors.textLight} />
        </TouchableOpacity>
      </View>
      <View style={mp.searchBox}>
        <Ionicons name="search" size={18} color={colors.textLight} />
        <TextInput
          style={mp.searchInput}
          value={search}
          onChangeText={onSearchChange}
          placeholder={`Search ${title.toLowerCase()}…`}
          placeholderTextColor={colors.textLight}
        />
        {search.length > 0 && (
          <TouchableOpacity onPress={() => onSearchChange('')} activeOpacity={0.7}>
            <Ionicons name="close-circle" size={18} color={colors.textLight} />
          </TouchableOpacity>
        )}
      </View>
      {loading ? (
        <View style={mp.center}><ActivityIndicator color={colors.primary} /></View>
      ) : (
        <FlatList
          data={items}
          keyExtractor={i => i.id}
          renderItem={({ item }) => {
            const isSelected = picked.has(item.id);
            const sub = renderSub?.(item);
            return (
              <TouchableOpacity style={mp.row} onPress={() => toggle(item)} activeOpacity={0.7}>
                <View style={[mp.checkbox, isSelected && mp.checkboxChecked]}>
                  {isSelected && <Ionicons name="checkmark" size={14} color={colors.white} />}
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={mp.itemName}>{item.name}</Text>
                  {sub && <Text style={mp.itemSub}>{sub}</Text>}
                </View>
              </TouchableOpacity>
            );
          }}
          ListEmptyComponent={
            <View style={mp.center}>
              <Text style={mp.empty}>No results found</Text>
            </View>
          }
        />
      )}
      <View style={mp.footer}>
        <Text style={mp.count}>{picked.size} selected</Text>
        <TouchableOpacity style={mp.confirmBtn} onPress={confirm} activeOpacity={0.8}>
          <Text style={mp.confirmText}>Confirm</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  if (Platform.OS === 'web') {
    if (!visible) return null;
    return (
      <View style={mp.webOverlay}>
        <TouchableOpacity style={mp.webBackdrop} activeOpacity={1} onPress={onClose} />
        <View style={mp.webSheet}>{content}</View>
      </View>
    );
  }

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="formSheet">
      {content}
    </Modal>
  );
};

const getMp = () => StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border },
  title: { fontSize: typography.fontSize.lg, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  searchBox: { flexDirection: 'row', alignItems: 'center', margin: spacing.md, borderWidth: 1, borderColor: colors.border, borderRadius: 8, paddingHorizontal: spacing.sm, gap: spacing.xs },
  searchInput: { flex: 1, paddingVertical: spacing.sm, fontSize: typography.fontSize.base, color: colors.textDark, fontFamily: typography.fontFamily.regular },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },
  empty: { color: colors.textLight, fontFamily: typography.fontFamily.regular },
  row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: spacing.md, paddingVertical: spacing.sm, gap: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border + '50' },
  checkbox: { width: 22, height: 22, borderRadius: 6, borderWidth: 2, borderColor: colors.border, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  checkboxChecked: { backgroundColor: colors.primary, borderColor: colors.primary },
  itemName: { fontSize: typography.fontSize.base, color: colors.textDark, fontFamily: typography.fontFamily.regular },
  itemSub: { fontSize: typography.fontSize.xs, color: colors.textSecondary, fontFamily: typography.fontFamily.regular, marginTop: 1 },
  footer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.md, borderTopWidth: 1, borderTopColor: colors.border },
  count: { fontSize: typography.fontSize.sm, color: colors.textSecondary, fontFamily: typography.fontFamily.regular },
  confirmBtn: { backgroundColor: colors.primary, borderRadius: 8, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  confirmText: { color: colors.white, fontWeight: '600', fontFamily: typography.fontFamily.semibold, fontSize: typography.fontSize.base },
  webOverlay: { ...StyleSheet.absoluteFillObject, zIndex: 10 },
  webBackdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.5)' },
  webSheet: { position: 'absolute' as any, top: spacing.xl * 2, left: spacing.xl * 3, right: spacing.xl * 3, bottom: spacing.xl * 2, backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.lg, overflow: 'hidden', ...designTokens.shadows.lg },
});

// ─── Selected tag pills row ───────────────────────────────────────────────────

const TagRow: React.FC<{ items: NamedItem[]; onRemove: (id: string) => void }> = ({ items, onRemove }) => {
  const tag = useThemedStyles(getTag);
  if (!items.length) return null;
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs }}>
      {items.map(item => (
        <View key={item.id} style={tag.pill}>
          <Text style={tag.label}>{item.name}</Text>
          <TouchableOpacity onPress={() => onRemove(item.id)} activeOpacity={0.7} style={{ marginLeft: 4 }}>
            <Ionicons name="close-circle" size={14} color={colors.primary} />
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );
};

const getTag = () => StyleSheet.create({
  pill: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.primary + '15', borderRadius: 20, paddingHorizontal: spacing.sm, paddingVertical: 3, borderWidth: 1, borderColor: colors.primary + '40' },
  label: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },
});

// ─── Coupon form ──────────────────────────────────────────────────────────────

const CouponFormContent: React.FC<{
  form: CouponForm;
  saving: boolean;
  isEdit: boolean;
  onClose: () => void;
  onChange: React.Dispatch<React.SetStateAction<CouponForm>>;
  onSubmit: () => void;
}> = ({ form, saving, isEdit, onClose, onChange, onSubmit }) => {
  const s = useThemedStyles(getS);
  const zs = useThemedStyles(getZs);
  // Contact picker state — tracks the "currently selected" contact in the ContactSelector widget
  // so we can add it to the list; reset to null after adding
  const [pickerContact, setPickerContact] = useState<any>(null);

  // Catalogue picker state
  const [showCataloguePicker, setShowCataloguePicker] = useState(false);
  const [catalogueSearch, setCatalogueSearch] = useState('');
  const [catalogueItems, setCatalogueItems] = useState<NamedItem[]>([]);
  const [catalogueLoading, setCatalogueLoading] = useState(false);
  const catalogueTimer = useRef<any>(null);

  // Product picker (reuses ProductSelectorModal)
  const [showProductPicker, setShowProductPicker] = useState(false);

  // Zone picker state
  const [zonePickerVisible, setZonePickerVisible] = useState(false);
  const [zonePickerMode, setZonePickerMode] = useState<'country' | 'city' | 'radius'>('city');
  const [editingZoneIndex, setEditingZoneIndex] = useState<number | null>(null);

  const openZonePicker = (zoneIndex: number | null, mode: 'country' | 'city' | 'radius') => {
    setEditingZoneIndex(zoneIndex);
    setZonePickerMode(mode);
    setZonePickerVisible(true);
  };

  const handleZoneConfirm = (result: ZoneResult) => {
    const geo: Partial<CouponZone> =
      result.mode === 'city' || result.mode === 'country'
        ? { mode: result.mode, cities: [result.cityName], center: result.center, polygon: result.polygon }
        : { mode: 'radius', center: result.center, radiusKm: result.radiusKm };

    onChange(prev => {
      const zones = [...prev.zones];
      const existing = editingZoneIndex !== null ? zones[editingZoneIndex] : undefined;
      const zone: CouponZone = { ...geo, name: result.name, value: existing?.value, type: existing?.type, enabled: existing?.enabled };
      if (editingZoneIndex !== null) zones[editingZoneIndex] = zone;
      else zones.push(zone);
      return { ...prev, zones };
    });
    setZonePickerVisible(false);
    setEditingZoneIndex(null);
  };


  const loadCatalogues = async (q: string) => {
    setCatalogueLoading(true);
    try {
      const res = await apiService.getCatalogues({ search: q || undefined, limit: 50 });
      if (res.statusCode === 1000) {
        const list: any[] = res.data?.catalogues ?? [];
        setCatalogueItems(list.map(c => ({ id: c._id, name: c.name })));
      }
    } catch (_) {}
    finally { setCatalogueLoading(false); }
  };


  const onCatalogueSearch = (q: string) => {
    setCatalogueSearch(q);
    clearTimeout(catalogueTimer.current);
    catalogueTimer.current = setTimeout(() => loadCatalogues(q), 350);
  };

  const addContact = (contact: any) => {
    const id = contact._id ?? contact.id ?? '';
    const name = contact.name ?? contact.fullName ?? id;
    if (!id) return;
    onChange(p => {
      if (p.contacts.some(c => c.id === id)) return p;
      return { ...p, contacts: [...p.contacts, { id, name }] };
    });
    setPickerContact(null);
  };

  const openCataloguePicker = () => {
    setCatalogueSearch('');
    loadCatalogues('');
    setShowCataloguePicker(true);
  };

  const toggleProductType = (t: ProductType) =>
    onChange(p => ({
      ...p,
      productTypes: p.productTypes.includes(t) ? p.productTypes.filter(x => x !== t) : [...p.productTypes, t],
    }));

  const removeContact = (id: string) => onChange(p => ({ ...p, contacts: p.contacts.filter(c => c.id !== id) }));
  const removeProduct = (id: string) => onChange(p => ({ ...p, products: p.products.filter(c => c.id !== id) }));
  const removeCatalogue = (id: string) => onChange(p => ({ ...p, catalogues: p.catalogues.filter(c => c.id !== id) }));

  return (
    <>
      <View style={s.modalHeader}>
        <Text style={s.modalTitle}>{isEdit ? 'Edit Coupon' : 'New Coupon'}</Text>
        <TouchableOpacity onPress={onClose} activeOpacity={0.7}>
          <Ionicons name="close" size={24} color={colors.textLight} />
        </TouchableOpacity>
      </View>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={s.formContent} keyboardShouldPersistTaps="handled">

        {/* ── Code ── */}
        <View style={s.field}>
          <Text style={s.fieldLabel}>Coupon Code *</Text>
          <TextInput
            style={s.input}
            value={form.code}
            onChangeText={v => onChange(p => ({ ...p, code: v.toUpperCase() }))}
            placeholder="e.g. SAVE20"
            placeholderTextColor={colors.textLight}
            autoCapitalize="characters"
          />
        </View>

        {/* ── Type + Value ── */}
        <Text style={s.fieldLabel}>Discount Type</Text>
        <View style={s.chipRow}>
          {(['flat', 'percentage'] as const).map(type => (
            <TouchableOpacity
              key={type}
              style={[s.chip, form.type === type && s.chipActive]}
              onPress={() => onChange(p => ({ ...p, type }))}
              activeOpacity={0.7}
            >
              <Text style={[s.chipText, form.type === type && s.chipTextActive]}>
                {type === 'flat' ? 'Fixed Amount' : 'Percentage'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        <View style={s.field}>
          <Text style={s.fieldLabel}>Value * ({form.type === 'percentage' ? '%' : 'amount'})</Text>
          <TextInput
            style={s.input}
            value={form.value}
            onChangeText={v => onChange(p => ({ ...p, value: v }))}
            keyboardType="decimal-pad"
            placeholder="0"
            placeholderTextColor={colors.textLight}
          />
        </View>

        {/* ── Eligibility ── */}
        <Text style={s.sectionLabel}>Eligibility</Text>

        <View style={s.field}>
          <Text style={s.fieldLabel}>Min Order Amount (optional)</Text>
          <TextInput
            style={s.input}
            value={form.minOrderAmount}
            onChangeText={v => onChange(p => ({ ...p, minOrderAmount: v }))}
            keyboardType="decimal-pad"
            placeholder="No minimum"
            placeholderTextColor={colors.textLight}
          />
        </View>

        <DatePickerField
          label="Start Date (optional)"
          value={form.startsAt}
          onChange={d => onChange(p => ({ ...p, startsAt: d }))}
          placeholder="No start date"
        />
        <DatePickerField
          label="Expiry Date (optional)"
          value={form.expiresAt}
          onChange={d => onChange(p => ({ ...p, expiresAt: d }))}
          placeholder="No expiry"
          minimumDate={form.startsAt ?? undefined}
        />

        {/* ── Targeting ── */}
        <Text style={s.sectionLabel}>Targeting</Text>

        <View style={s.switchRow}>
          <View style={{ flex: 1 }}>
            <Text style={s.switchLabel}>Public</Text>
            <Text style={s.hint}>Anyone can use this coupon</Text>
          </View>
          <Switch
            value={form.isPublic}
            onValueChange={v => onChange(p => ({ ...p, isPublic: v }))}
            trackColor={{ true: colors.primary }}
          />
        </View>

        {/* Catalogue targeting */}
        <View style={s.field}>
          <Text style={s.fieldLabel}>Catalogues (optional)</Text>
          <Text style={s.hint}>Coupon applies to any product in these catalogues. Dynamically includes future products added to them.</Text>
          <TagRow items={form.catalogues} onRemove={removeCatalogue} />
          <TouchableOpacity style={s.pickerBtn} onPress={openCataloguePicker} activeOpacity={0.7}>
            <Ionicons name="albums-outline" size={16} color={colors.primary} />
            <Text style={s.pickerBtnText}>
              {form.catalogues.length ? 'Edit Catalogues' : 'Add Catalogues'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Specific products targeting */}
        <View style={s.field}>
          <Text style={s.fieldLabel}>Specific Products (optional)</Text>
          <Text style={s.hint}>Coupon only applies if the order contains at least one of these products.</Text>
          <TagRow items={form.products} onRemove={removeProduct} />
          <TouchableOpacity style={s.pickerBtn} onPress={() => setShowProductPicker(true)} activeOpacity={0.7}>
            <Ionicons name="cube-outline" size={16} color={colors.primary} />
            <Text style={s.pickerBtnText}>
              {form.products.length ? 'Edit Products' : 'Add Products'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Product type filter */}
        <View style={s.field}>
          <Text style={s.fieldLabel}>Product Types (optional — empty = all types)</Text>
          <View style={s.chipRow}>
            {PRODUCT_TYPES.map(t => (
              <TouchableOpacity
                key={t}
                style={[s.chip, form.productTypes.includes(t) && s.chipActive]}
                onPress={() => toggleProductType(t)}
                activeOpacity={0.7}
              >
                <Text style={[s.chipText, form.productTypes.includes(t) && s.chipTextActive]}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={s.switchRow}>
          <View style={{ flex: 1 }}>
            <Text style={s.switchLabel}>First-Time Customers Only</Text>
            <Text style={s.hint}>Only applies to contacts with no prior orders</Text>
          </View>
          <Switch
            value={form.firstTimeOnly}
            onValueChange={v => onChange(p => ({ ...p, firstTimeOnly: v }))}
            trackColor={{ true: colors.primary }}
          />
        </View>

        {/* ── Usage Limits ── */}
        <Text style={s.sectionLabel}>Usage Limits</Text>

        <View style={s.field}>
          <Text style={s.fieldLabel}>Max Total Uses (optional)</Text>
          <TextInput
            style={s.input}
            value={form.maxUsageTotal}
            onChangeText={v => onChange(p => ({ ...p, maxUsageTotal: v }))}
            keyboardType="number-pad"
            placeholder="Unlimited"
            placeholderTextColor={colors.textLight}
          />
        </View>
        <View style={s.field}>
          <Text style={s.fieldLabel}>Max Uses Per Customer (optional)</Text>
          <TextInput
            style={s.input}
            value={form.maxUsagePerContact}
            onChangeText={v => onChange(p => ({ ...p, maxUsagePerContact: v }))}
            keyboardType="number-pad"
            placeholder="Unlimited"
            placeholderTextColor={colors.textLight}
          />
        </View>

        <View style={s.switchRow}>
          <Text style={s.switchLabel}>Active</Text>
          <Switch
            value={form.isActive}
            onValueChange={v => onChange(p => ({ ...p, isActive: v }))}
            trackColor={{ true: colors.primary }}
          />
        </View>

        {/* ── Zone pricing ── */}
        <View style={s.divider} />
        <View style={{ gap: spacing.sm }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={s.sectionLabel}>Location Pricing</Text>
            <Text style={{ fontSize: typography.fontSize.xs, color: colors.textLight }}>First match wins</Text>
          </View>
          {form.zones.map((zone, zi) => (
            <View key={zi} style={zs.zoneCard}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                <View style={{ flex: 1 }}>
                  <Text style={zs.zoneName}>{zone.name}</Text>
                  <Text style={zs.zoneMeta}>
                    {zone.mode === 'radius' ? `Radius ${zone.radiusKm ?? '?'} km` : zone.cities?.join(', ') ?? zone.mode ?? ''}
                  </Text>
                </View>
                <TouchableOpacity onPress={() => openZonePicker(zi, zone.mode ?? 'city')} style={{ padding: 4 }} activeOpacity={0.7}>
                  <Ionicons name="map-outline" size={16} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => onChange(p => ({ ...p, zones: p.zones.filter((_, i) => i !== zi) }))} style={{ padding: 4 }} activeOpacity={0.7}>
                  <Ionicons name="trash-outline" size={16} color={colors.error} />
                </TouchableOpacity>
              </View>
              <View style={{ flexDirection: 'row', gap: spacing.sm, alignItems: 'flex-start' }}>
                <View style={{ flex: 1 }}>
                  <Text style={s.fieldLabel}>{form.type === 'percentage' ? 'Rate (%)' : 'Amount'} override</Text>
                  <TextInput
                    style={s.input}
                    value={zone.value != null ? String(zone.value) : ''}
                    onChangeText={v => onChange(p => {
                      const zones = [...p.zones];
                      zones[zi] = { ...zones[zi], value: v === '' ? undefined : parseFloat(v) || 0 };
                      return { ...p, zones };
                    })}
                    keyboardType="decimal-pad"
                    placeholder="Default"
                    placeholderTextColor={colors.textLight}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={s.fieldLabel}>Type override</Text>
                  <View style={{ flexDirection: 'row', gap: spacing.xs }}>
                    {(['flat', 'percentage'] as const).map(tp => (
                      <TouchableOpacity
                        key={tp}
                        style={[s.chip, (zone.type ?? form.type) === tp && s.chipActive]}
                        onPress={() => onChange(p => {
                          const zones = [...p.zones];
                          zones[zi] = { ...zones[zi], type: tp === form.type ? undefined : tp };
                          return { ...p, zones };
                        })}
                        activeOpacity={0.7}>
                        <Text style={[s.chipText, (zone.type ?? form.type) === tp && s.chipTextActive]}>{tp === 'flat' ? 'Flat' : '%'}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
                <View style={{ alignItems: 'center' }}>
                  <Text style={s.fieldLabel}>Disable</Text>
                  <Switch
                    value={zone.enabled === false}
                    onValueChange={v => onChange(p => {
                      const zones = [...p.zones];
                      zones[zi] = { ...zones[zi], enabled: v ? false : undefined };
                      return { ...p, zones };
                    })}
                    trackColor={{ true: colors.error }}
                  />
                </View>
              </View>
            </View>
          ))}
          <View style={{ flexDirection: 'row', gap: spacing.xs, flexWrap: 'wrap' }}>
            {(['country', 'city', 'radius'] as const).map(mode => (
              <TouchableOpacity key={mode} style={zs.addZoneBtn} onPress={() => openZonePicker(null, mode)} activeOpacity={0.7}>
                <Ionicons name={mode === 'country' ? 'globe-outline' : mode === 'city' ? 'location-outline' : 'radio-button-on-outline'} size={14} color={colors.primary} />
                <Text style={zs.addZoneBtnText}>{mode === 'country' ? 'Country' : mode === 'city' ? 'City' : 'Radius'}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <TouchableOpacity style={s.saveBtn} onPress={onSubmit} disabled={saving} activeOpacity={0.8}>
          {saving
            ? <ActivityIndicator color={colors.white} />
            : <Text style={s.saveBtnText}>{isEdit ? 'Save Changes' : 'Create Coupon'}</Text>}
        </TouchableOpacity>
      </ScrollView>

      {/* Contact whitelist — rendered outside ScrollView so dropdown overlays other content */}
      {!form.isPublic && (
        <View style={s.contactPickerPanel}>
          <Text style={s.fieldLabel}>Specific Contacts</Text>
          <Text style={s.hint}>Only these contacts can use this coupon.</Text>
          <TagRow items={form.contacts} onRemove={removeContact} />
          <Text style={[s.hint, { marginTop: spacing.xs }]}>Search and select a contact to add:</Text>
          <ContactSelector
            mode="edit"
            variant="simple"
            contact={pickerContact}
            onContactSelect={addContact}
          />
        </View>
      )}

      {/* Catalogue picker */}
      <MultiPicker
        visible={showCataloguePicker}
        title="Select Catalogues"
        selected={form.catalogues}
        items={catalogueItems}
        loading={catalogueLoading}
        search={catalogueSearch}
        onSearchChange={onCatalogueSearch}
        onClose={() => setShowCataloguePicker(false)}
        onConfirm={items => onChange(p => ({ ...p, catalogues: items }))}
      />

      {/* Product picker — resolves names from API after selection */}
      <ProductSelectorModal
        visible={showProductPicker}
        onClose={() => setShowProductPicker(false)}
        title="Select Products"
        confirmText="Add Selected"
        allowMultiple
        onConfirm={async (selectedIds) => {
          setShowProductPicker(false);
          // Keep existing selections, add new ones with resolved names
          const existing = new Map(form.products.map(p => [p.id, p.name]));
          const newIds = selectedIds.filter(id => !existing.has(id));
          const resolved = await Promise.all(
            newIds.map(async id => {
              try {
                const res = await apiService.getProductById(id);
                const name = res.statusCode === 1000 ? (res.data?.name ?? id) : id;
                return { id, name };
              } catch {
                return { id, name: id };
              }
            })
          );
          // Build final list: only ids still in selectedIds
          const selectedSet = new Set(selectedIds);
          const kept = form.products.filter(p => selectedSet.has(p.id));
          onChange(p => ({ ...p, products: [...kept, ...resolved] }));
        }}
      />

      {/* Zone map picker */}
      <ZoneMapPicker
        visible={zonePickerVisible}
        onClose={() => { setZonePickerVisible(false); setEditingZoneIndex(null); }}
        initialMode={zonePickerMode}
        initialData={editingZoneIndex !== null ? (() => {
          const z = form.zones[editingZoneIndex];
          if (!z) return undefined;
          if (z.mode === 'radius') return { mode: 'radius' as const, name: z.name, price: z.value ?? 0, center: z.center, radiusKm: z.radiusKm };
          return { mode: (z.mode ?? 'city') as 'city' | 'country', name: z.name, price: z.value ?? 0, cityName: z.cities?.[0] ?? '', center: z.center };
        })() : undefined}
        onConfirm={handleZoneConfirm}
      />
    </>
  );
};

// ─── Main screen ──────────────────────────────────────────────────────────────

export const CouponsScreen: React.FC = () => {
  const s = useThemedStyles(getS);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [showLeftPane] = useState(Platform.OS === 'web');
  const [showRightPane] = useState(Platform.OS === 'web');

  const [coupons, setCoupons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<any | null>(null);
  const [form, setForm] = useState<CouponForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const openCreate = () => {
    setEditingCoupon(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = async (coupon: any) => {
    setEditingCoupon(coupon);
    const base = couponToForm(coupon);
    setForm(base);
    setShowForm(true);

    // Resolve real names for IDs in the background then update form
    const resolveNames = async () => {
      const updates: Partial<CouponForm> = {};

      // Contacts
      if (coupon.contactIds?.length) {
        try {
          const res = await apiService.getUnifiedContacts({ limit: 200, offset: 0 });
          if (res.statusCode === 1000) {
            const all: any[] = res.data?.contacts ?? res.data ?? [];
            const map = new Map(all.map((c: any) => [c._id ?? c.id, c.name ?? c.fullName]));
            updates.contacts = coupon.contactIds.map((id: string) => ({ id, name: map.get(id) ?? id }));
          }
        } catch (_) {}
      }

      // Products
      if (coupon.productIds?.length) {
        const resolved = await Promise.all(
          coupon.productIds.map(async (id: string) => {
            try {
              const res = await apiService.getProductById(id);
              return { id, name: res.statusCode === 1000 ? (res.data?.name ?? id) : id };
            } catch { return { id, name: id }; }
          })
        );
        updates.products = resolved;
      }

      // Catalogues
      if (coupon.catalogueIds?.length) {
        try {
          const res = await apiService.getCatalogues({ limit: 200 });
          if (res.statusCode === 1000) {
            const all: any[] = res.data?.catalogues ?? [];
            const map = new Map(all.map((c: any) => [c._id, c.name]));
            updates.catalogues = coupon.catalogueIds.map((id: string) => ({ id, name: map.get(id) ?? id }));
          }
        } catch (_) {}
      }

      if (Object.keys(updates).length) {
        setForm(prev => ({ ...prev, ...updates }));
      }
    };

    resolveNames();
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingCoupon(null);
    setForm(EMPTY_FORM);
  };

  const loadCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.listCoupons();
      if (res.statusCode === 1000) setCoupons(res.data ?? []);
    } catch (e: any) {
      showNotification('error', 'Error', e.message);
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { loadCoupons(); }, [loadCoupons]);

  const buildPayload = () => ({
    code: form.code.trim().toUpperCase(),
    type: form.type,
    value: parseFloat(form.value),
    minOrderAmount: form.minOrderAmount ? parseFloat(form.minOrderAmount) : undefined,
    startsAt: form.startsAt ?? undefined,
    expiresAt: form.expiresAt ?? undefined,
    isPublic: form.isPublic,
    firstTimeOnly: form.firstTimeOnly,
    productTypes: form.productTypes,
    contactIds: form.contacts.map(c => c.id),
    productIds: form.products.map(p => p.id),
    catalogueIds: form.catalogues.map(c => c.id),
    maxUsageTotal: form.maxUsageTotal ? parseInt(form.maxUsageTotal) : undefined,
    maxUsagePerContact: form.maxUsagePerContact ? parseInt(form.maxUsagePerContact) : undefined,
    isActive: form.isActive,
    zones: form.zones,
  });

  const validate = () => {
    if (!form.code.trim()) { showNotification('warning', 'Required', 'Enter a coupon code'); return false; }
    if (!form.value || isNaN(parseFloat(form.value))) { showNotification('warning', 'Required', 'Enter a discount value'); return false; }
    return true;
  };

  const createCoupon = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await apiService.createCoupon(buildPayload());
      if (res.statusCode === 1000) {
        showNotification('success', 'Created', 'Coupon created successfully');
        closeForm();
        loadCoupons();
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to create coupon');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveCoupon = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await apiService.updateCoupon(editingCoupon._id, buildPayload());
      if (res.statusCode === 1000) {
        showNotification('success', 'Saved', 'Coupon updated');
        closeForm();
        loadCoupons();
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to save coupon');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteCoupon = (id: string, code: string) => {
    Alert.alert('Delete Coupon', `Delete coupon "${code}"?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete', style: 'destructive', onPress: async () => {
          try {
            const res = await apiService.deleteCoupon(id);
            if (res.statusCode === 1000) {
              showNotification('success', 'Deleted', 'Coupon deleted');
              loadCoupons();
            } else {
              showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to delete');
            }
          } catch (e: any) {
            showNotification('error', 'Error', e.message || 'Failed to delete coupon');
          }
        },
      },
    ]);
  };

  const isExpired = (expiresAt?: string) => expiresAt && new Date(expiresAt) < new Date();
  const isNotStarted = (startsAt?: string) => startsAt && new Date(startsAt) > new Date();

  const listContent = (
    <>
      {loading ? (
        <View style={s.center}><ActivityIndicator size="large" color={colors.primary} /></View>
      ) : coupons.length === 0 ? (
        <View style={s.center}>
          <Ionicons name="pricetag-outline" size={56} color={colors.border} />
          <Text style={s.emptyTitle}>No Coupons Yet</Text>
          <Text style={s.emptyDesc}>Create discount codes for your customers</Text>
          <TouchableOpacity style={s.createBtn} onPress={openCreate}>
            <Text style={s.createBtnText}>Create Coupon</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <ScrollView contentContainerStyle={s.listContent}>
          {coupons.map(coupon => (
            <View key={coupon._id} style={[s.card, !coupon.isActive && s.cardInactive]}>
              <View style={s.cardTop}>
                <View style={s.codeBox}>
                  <Text style={s.codeText}>{coupon.code}</Text>
                </View>
                <View style={s.cardBadges}>
                  {isExpired(coupon.expiresAt) && <View style={s.expiredBadge}><Text style={s.expiredText}>Expired</Text></View>}
                  {isNotStarted(coupon.startsAt) && <View style={s.scheduledBadge}><Text style={s.scheduledText}>Scheduled</Text></View>}
                  {!coupon.isActive && <View style={s.inactiveBadge}><Text style={s.inactiveText}>Inactive</Text></View>}
                  {!coupon.isPublic && <View style={s.privateBadge}><Text style={s.privateText}>Private</Text></View>}
                </View>
                <TouchableOpacity onPress={() => openEdit(coupon)} style={s.cardAction} activeOpacity={0.7}>
                  <Ionicons name="pencil-outline" size={17} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => deleteCoupon(coupon._id, coupon.code)} style={s.cardAction} activeOpacity={0.7}>
                  <Ionicons name="trash-outline" size={17} color={colors.error} />
                </TouchableOpacity>
              </View>
              <View style={s.cardDetails}>
                <Text style={s.valueText}>
                  {coupon.type === 'percentage' ? `${coupon.value}% off` : `${coupon.value} off`}
                </Text>
                {coupon.minOrderAmount > 0 && (
                  <Text style={s.detailText}>Min order: {coupon.minOrderAmount}</Text>
                )}
                {coupon.maxUsageTotal
                  ? <Text style={s.detailText}>{coupon.usedCount}/{coupon.maxUsageTotal} used</Text>
                  : <Text style={s.detailText}>{coupon.usedCount} used</Text>}
                {coupon.firstTimeOnly && <Text style={s.detailText}>First-time only</Text>}
                {coupon.contactIds?.length > 0 && (
                  <Text style={s.detailText}>{coupon.contactIds.length} contact(s)</Text>
                )}
                {coupon.productIds?.length > 0 && (
                  <Text style={s.detailText}>{coupon.productIds.length} product(s)</Text>
                )}
                {coupon.catalogueIds?.length > 0 && (
                  <Text style={s.detailText}>{coupon.catalogueIds.length} catalogue(s)</Text>
                )}
                {coupon.startsAt && (
                  <Text style={s.detailText}>From: {new Date(coupon.startsAt).toLocaleDateString()}</Text>
                )}
                {coupon.expiresAt && (
                  <Text style={s.detailText}>Expires: {new Date(coupon.expiresAt).toLocaleDateString()}</Text>
                )}
              </View>
            </View>
          ))}
        </ScrollView>
      )}
    </>
  );

  const formContent = (
    <CouponFormContent
      form={form}
      saving={saving}
      isEdit={!!editingCoupon}
      onClose={closeForm}
      onChange={setForm}
      onSubmit={editingCoupon ? saveCoupon : createCoupon}
    />
  );

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      showLeftPane={showLeftPane} onLeftPaneClose={() => {}} leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={showRightPane} onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title="Coupons"
          onBack={() => navigateTo('settings')}
          actions={[{ icon: 'add', primary: true, onPress: openCreate, accessibilityLabel: 'Create coupon' }]}
        />
      }
    >
      {Platform.OS === 'web' ? (
        <View style={s.webContainer}>
          {listContent}
          {showForm && (
            <>
              <TouchableOpacity style={s.webBackdrop} activeOpacity={1} onPress={closeForm} />
              <View style={s.webSheet}>{formContent}</View>
            </>
          )}
        </View>
      ) : (
        <>
          {listContent}
          <Modal visible={showForm} animationType="slide" presentationStyle="formSheet">
            <View style={s.modal}>{formContent}</View>
          </Modal>
        </>
      )}
    </ScreenLayout>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const getS = () => StyleSheet.create({
  webContainer: { flex: 1, position: 'relative' as any },
  webBackdrop: { position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1 },
  webSheet: { position: 'absolute' as any, top: spacing.xl, left: spacing.xl, right: spacing.xl, bottom: spacing.xl, backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.lg, overflow: 'hidden', zIndex: 2, ...designTokens.shadows.lg },
  modal: { flex: 1, backgroundColor: colors.background.surface },

  center: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: spacing.sm, padding: spacing.xl },
  emptyTitle: { fontSize: typography.fontSize.lg, fontWeight: '600', color: colors.textDark, marginTop: spacing.md, fontFamily: typography.fontFamily.semibold },
  emptyDesc: { fontSize: typography.fontSize.sm, color: colors.textSecondary, textAlign: 'center', fontFamily: typography.fontFamily.regular },
  createBtn: { backgroundColor: colors.primary, borderRadius: 10, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm, marginTop: spacing.md },
  createBtnText: { fontSize: typography.fontSize.base, color: colors.white, fontWeight: '600', fontFamily: typography.fontFamily.semibold },

  listContent: { padding: spacing.md, gap: spacing.sm, paddingBottom: 40 },
  card: { borderWidth: 1, borderColor: colors.border, borderRadius: 12, backgroundColor: colors.background.surface, overflow: 'hidden', ...designTokens.shadows.sm },
  cardInactive: { opacity: 0.55 },
  cardTop: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, padding: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border },
  codeBox: { backgroundColor: colors.primary + '15', borderRadius: 6, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  codeText: { fontSize: typography.fontSize.sm, color: colors.primary, fontWeight: '700', fontFamily: 'monospace' },
  cardBadges: { flex: 1, flexDirection: 'row', gap: spacing.xs, flexWrap: 'wrap' },
  cardAction: { padding: spacing.xs },

  expiredBadge: { backgroundColor: colors.error + '20', borderRadius: 4, paddingHorizontal: spacing.xs, paddingVertical: 2 },
  expiredText: { fontSize: typography.fontSize.xs, color: colors.error, fontWeight: '600', fontFamily: typography.fontFamily.semibold },
  scheduledBadge: { backgroundColor: colors.primary + '20', borderRadius: 4, paddingHorizontal: spacing.xs, paddingVertical: 2 },
  scheduledText: { fontSize: typography.fontSize.xs, color: colors.primary, fontWeight: '600', fontFamily: typography.fontFamily.semibold },
  inactiveBadge: { backgroundColor: colors.border, borderRadius: 4, paddingHorizontal: spacing.xs, paddingVertical: 2 },
  inactiveText: { fontSize: typography.fontSize.xs, color: colors.textLight, fontWeight: '600', fontFamily: typography.fontFamily.semibold },
  privateBadge: { backgroundColor: colors.warning + '20', borderRadius: 4, paddingHorizontal: spacing.xs, paddingVertical: 2 },
  privateText: { fontSize: typography.fontSize.xs, color: colors.warning, fontWeight: '600', fontFamily: typography.fontFamily.semibold },

  cardDetails: { padding: spacing.sm, flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  valueText: { fontSize: typography.fontSize.base, color: colors.textDark, fontWeight: '600', fontFamily: typography.fontFamily.semibold },
  detailText: { fontSize: typography.fontSize.xs, color: colors.textSecondary, fontFamily: typography.fontFamily.regular },

  // Form
  contactPickerPanel: { borderTopWidth: 1, borderTopColor: colors.border, padding: spacing.md, gap: spacing.xs, backgroundColor: colors.background.surface, zIndex: 10 },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border, ...designTokens.shadows.sm },
  modalTitle: { fontSize: typography.fontSize.lg, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  formContent: { padding: spacing.md, gap: spacing.md, paddingBottom: 40 },
  sectionLabel: { fontSize: typography.fontSize.xs, fontWeight: '700', color: colors.textLight, fontFamily: typography.fontFamily.semibold, textTransform: 'uppercase', letterSpacing: 0.8, marginTop: spacing.xs },
  field: { gap: spacing.xs },
  fieldLabel: { fontSize: typography.fontSize.sm, color: colors.textSecondary, fontWeight: '600', fontFamily: typography.fontFamily.semibold },
  hint: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  input: { borderWidth: 1, borderColor: colors.border, borderRadius: 8, paddingHorizontal: spacing.sm, paddingVertical: spacing.sm, fontSize: typography.fontSize.base, color: colors.textDark, backgroundColor: colors.background.surface, fontFamily: typography.fontFamily.regular },
  chipRow: { flexDirection: 'row', gap: spacing.xs, flexWrap: 'wrap' },
  chip: { paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: 20, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  chipActive: { borderColor: colors.primary, backgroundColor: colors.primary + '15' },
  chipText: { fontSize: typography.fontSize.sm, color: colors.textSecondary, fontFamily: typography.fontFamily.regular },
  chipTextActive: { color: colors.primary, fontWeight: '600', fontFamily: typography.fontFamily.semibold },
  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  switchLabel: { fontSize: typography.fontSize.base, color: colors.textDark, fontFamily: typography.fontFamily.regular },
  pickerBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, borderWidth: 1, borderColor: colors.primary, borderRadius: 8, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, alignSelf: 'flex-start', marginTop: spacing.xs },
  pickerBtnText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  saveBtn: { backgroundColor: colors.primary, borderRadius: 10, paddingVertical: spacing.sm, alignItems: 'center', marginTop: spacing.sm },
  saveBtnText: { fontSize: typography.fontSize.base, color: colors.white, fontWeight: '600', fontFamily: typography.fontFamily.semibold },
  divider: { height: 1, backgroundColor: colors.border, marginVertical: spacing.xs },
});

const getZs = () => StyleSheet.create({
  zoneCard: { borderWidth: 1, borderColor: colors.border, borderRadius: 8, padding: spacing.sm, gap: spacing.sm, backgroundColor: '#f9f9f9' },
  zoneName: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.textDark },
  zoneMeta: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  addZoneBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1, borderColor: colors.primary, borderRadius: 6, paddingHorizontal: spacing.sm, paddingVertical: 4 },
  addZoneBtnText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.medium },
});
